import { useIsWebsiteSelectorEnabled } from '@va/aaas/shared/selectors';
import { FEATURE_TOUR_IDS, TEST_IDS } from '@va/constants';
import { switchActiveWebsite } from '@va/dashboard/actions/app';
import { getAccountInformation } from '@va/dashboard/selectors/api';
import { getWebsite } from '@va/dashboard/selectors/core';
import { isTabletDevice } from '@va/dashboard/selectors/ui';
import { CircleArrowIcon, ExternalLinkIcon, GlobeIcon, RadioInputSelected } from '@va/icons';
import { useTranslate } from '@va/localization';
import { isAccountSettingsPageSelected } from '@va/standalone/shared/helpers';
import { Button, fontWeights, ParagraphWithTooltip } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { DropdownArrow } from '@va/util/components';
import { getUrlWithoutProtocolAndTrailingSlash, getWellFormedUrl } from '@va/util/helpers';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { WebsiteSelectorDropdown } from './WebsiteSelectorDropdown';

export const WebsiteSelector = () => {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const isTablet = useSelector(isTabletDevice);
  const website = useSelector(getWebsite);
  const translate = useTranslate();
  const dispatch = useDispatch();
  const url = useMemo(() => getUrlWithoutProtocolAndTrailingSlash(website?.url ?? ''), [website?.url]);

  const accountInfo = useSelector(getAccountInformation);

  const enabled = useIsWebsiteSelectorEnabled();

  const { pathname } = useLocation();

  const isAccountSettingsPage = isAccountSettingsPageSelected(pathname);

  if (!enabled) {
    return (
      <div className='relative flex items-center gap-3'>
        <div className='flex w-full truncate gap-3 h-12 items-center bg-white-snow rounded-15 px-3 lg:w-75'>
          <Icon />
          <ParagraphWithTooltip weight={fontWeights.medium}>{url}</ParagraphWithTooltip>
        </div>
        <Button
          tooltip={translate('topNav.goToWebsite')}
          color='secondary'
          icon={() => <ExternalLinkIcon />}
          onClick={() => {
            window.open(getWellFormedUrl(website.url));
          }}
        />
      </div>
    );
  }

  return (
    <div className='relative flex items-center gap-3'>
      <TooltipWrapper
        placement={isTablet ? 'bottom' : 'bottom-start'}
        interactive={true}
        arrow={false}
        outsidePress={() => {
          if (window.disableClickOutside) return false;
          return true;
        }}
        content={
          <WebsiteSelectorDropdown
            closeDropdown={() => {
              setIsDropdownVisible(false);
            }}
            userId={accountInfo?.id}
          />
        }
        tooltipClassNames='w-350px'
        trigger='click'
        useDefaultStyle={false}
        open={isDropdownVisible}
        unmountOnClose={false}
        onOpenChange={(open) => setIsDropdownVisible(open)}
      >
        <div className='flex w-full truncate'>
          <Button
            className={classNames('w-full lg:w-75', { isOpen: isDropdownVisible })}
            color='tertiary'
            icon={() => <Icon />}
            text={isAccountSettingsPage ? translate('dropdown.manageWebsites') : url}
            rightSideIcon={<DropdownArrow className='mr-auto ml-4' open={isDropdownVisible} />}
            dataTourId={FEATURE_TOUR_IDS.websiteSelectorBtn}
          />
        </div>
      </TooltipWrapper>

      {isAccountSettingsPage ? (
        <Button
          onClick={() => dispatch(switchActiveWebsite({ websiteId: website.id }))}
          color='tertiary'
          icon={(iconClassName) => <CircleArrowIcon className={iconClassName} color={'#696969'} />}
          text={translate('topBar.goBackBtn')}
          className='shrink-0'
          data-testid={TEST_IDS.generic.buttons.goToPrevWebsite}
        />
      ) : (
        <Button
          tooltip={translate('topNav.goToWebsite')}
          color='secondary'
          icon={() => <ExternalLinkIcon />}
          onClick={() => {
            window.open(getWellFormedUrl(website.url));
          }}
          dataTourId={FEATURE_TOUR_IDS.goToWebsiteBtn}
        />
      )}
    </div>
  );
};

const Icon = () => {
  return (
    <div className='relative flex items-center justify-center ml-2'>
      <GlobeIcon />
      <div className='absolute left-[-3px] flex items-center justify-center bg-white rounded-full'>
        <RadioInputSelected className='w-3 h-3' />
        <div className='absolute animate-circle w-10 h-10 bg-[#06a25a]'></div>
      </div>
    </div>
  );
};
